<template>
  <div class="h-full">
    <div v-if="loading" class="flex-grow flex items-center justify-center h-full">
      <Spinner class="w-8" />
    </div>
    <div v-else class="w-full pr-3 sm:pr-10 pl-2 pb-2 max-w-1000">
      <div class="text-13 text-semi-dark-text mt-1">{{ $t('admin.security.intro') }}</div>

      <div v-if="loadError" class="mt-4 border border-solid border-stroke rounded-8 p-4 flex items-center text-13">
        <Icon name="info" class="w-4 h-4 mr-2 flex-shrink-0 text-primary" />
        <span>{{ loadError }}</span>
      </div>

      <template v-if="keys">
        <!-- 1. Setup checks -->
        <div class="mt-6 text-18 font-500 mb-1">{{ $t('admin.security.setup.title') }}</div>
        <div class="text-13 text-semi-dark-text mb-2">{{ $t('admin.security.setup.guidance') }}</div>
        <div class="border border-solid border-stroke rounded-8">
          <div v-for="(item, index) in keys.checks || []" :key="item.name">
            <Divider v-if="index" dense />
            <div class="flex items-start p-4 text-13">
              <Icon :name="item.ok ? 'check_circle' : 'cancel'" class="w-5 h-5 mr-3 flex-shrink-0" :class="item.ok ? 'text-green-600' : 'text-alert'" />
              <div>
                <div class="font-600">{{ checkLabel(item.name) }}</div>
                <div :class="{ 'text-alert': !item.ok }">{{ item.message }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Root key -->
        <div class="mt-8 text-18 font-500 mb-1">{{ $t('admin.security.root.title') }}</div>
        <div class="text-13 text-semi-dark-text mb-2">{{ $t('admin.security.root.guidance') }}</div>
        <div class="border border-solid border-stroke rounded-8">
          <div v-for="item in alertSlots" :key="`alert-${item.slot}`" class="m-4 p-3 rounded-8 border border-solid border-alert bg-alert/10 text-alert flex items-start text-13">
            <Icon name="info" class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>{{ item.message }}</span>
          </div>

          <template v-if="hasRootKey">
            <div class="px-2">
              <TableElevated :items="rootKeyItems" :headers="rootKeyHeaders" class="text-13">
                <template v-slot:[`item.state`]="{ item }">
                  <Badge :success="item.state === 'active'" :warn="item.state === 'retiring'" :gray="item.state === 'retired'" :text="rootStateLabel(item.state)" />
                </template>
                <template v-slot:[`item.fingerprint`]="{ item }">
                  <span class="font-mono" :title="item.fingerprint">{{ shortFingerprint(item.fingerprint) }}</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <PillButton
                    v-if="canRetire(item)"
                    small
                    outlined
                    :text="$t('admin.security.root.retire')"
                    :loading="retiringGeneration === item.generation"
                    :disabled="retiringGeneration !== null"
                    @click="onRetireClick(item.generation)"
                  />
                </template>
              </TableElevated>
            </div>
            <div v-if="rootKeyItems.some(canRetire)" class="text-12 text-semi-dark-text px-4 pb-2">{{ $t('admin.security.root.retire_description') }}</div>
            <div v-for="item in retiredSlots" :key="`retired-${item.slot}`" class="flex items-start text-13 px-4 pb-2">
              <Icon name="info" class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-primary" />
              <span>{{ $t('admin.security.root.retired_notice', { generation: item.generation, slot: item.slot }) }}</span>
            </div>
            <Divider dense />
          </template>

          <div class="p-4 text-13">
            <div class="font-600 mb-2">{{ hasRootKey ? $t('admin.security.root.change_title') : $t('admin.security.root.setup_title') }}</div>
            <div v-if="changeBlockedReason" class="text-semi-dark-text mb-2">{{ changeBlockedReason }}</div>

            <ol v-if="!hasRootKey" class="list-decimal pl-5">
              <li class="mb-2">
                {{ $t('admin.security.root.step_create') }}
                <pre class="mt-1 p-3 bg-surface-dark border border-stroke rounded-md text-12 font-mono overflow-x-auto whitespace-pre">{{ CREATE_KEY_COMMAND }}</pre>
              </li>
              <li class="mb-2">{{ $t('admin.security.root.step_paste', slotSecret('A')) }}</li>
              <li>{{ $t('admin.security.root.step_check') }}</li>
            </ol>
            <template v-else-if="freeSlot && ['empty', 'retired'].includes(freeSlot.holds)">
              <div>{{ $t('admin.security.root.change_steps', slotSecret(freeSlot.slot)) }}</div>
              <div v-if="freeSlot.holds === 'retired'" class="mt-1">{{ $t('admin.security.root.change_replace_retired', { slot: freeSlot.slot, generation: freeSlot.generation }) }}</div>
              <pre class="mt-2 p-3 bg-surface-dark border border-stroke rounded-md text-12 font-mono overflow-x-auto whitespace-pre">{{ CREATE_KEY_COMMAND }}</pre>
            </template>

            <div v-if="newKeySlot" class="mt-4">
              <div>{{ $t('admin.security.root.new_key_found', { slot: newKeySlot.slot }) }}</div>
              <div class="font-mono text-14 font-600 mt-1 break-all">{{ newKeySlot.fingerprint }}</div>
              <div class="mt-3">{{ $t('admin.security.root.verify_fingerprint') }}</div>
              <pre class="mt-1 p-3 bg-surface-dark border border-stroke rounded-md text-12 font-mono overflow-x-auto whitespace-pre">{{ FINGERPRINT_COMMAND }}</pre>
            </div>

            <div class="flex items-center flex-wrap gap-3 mt-4">
              <PillButton
                v-if="newKeySlot"
                primary
                icon="lock"
                :text="hasRootKey ? $t('admin.security.root.use_key') : $t('admin.security.root.register')"
                :loading="savingRoot"
                :disabled="savingRoot || !!changeBlockedReason"
                @click="hasRootKey ? onUseKeyClick() : onRegisterClick()"
              />
              <PillButton outlined icon="refresh" :text="$t('admin.security.root.check_slots')" :loading="checkingSlots" :disabled="checkingSlots" @click="onCheckSlotsClick" />
            </div>
            <div v-if="rootResult" class="text-green-600 mt-3">{{ rootResult }}</div>
            <FormError v-if="rootError" :label="rootError" />
          </div>
        </div>

        <!-- 3. Projects and jobs -->
        <div class="mt-8 text-18 font-500 mb-2">{{ $t('admin.security.projects.title') }}</div>
        <div class="border border-solid border-stroke rounded-8">
          <div class="flex p-4 gap-10 flex-wrap">
            <div>
              <div class="text-12 text-gray-500">{{ $t('admin.security.projects.total') }}</div>
              <div class="font-600 text-18">{{ countText(keys.projects?.total) }}</div>
            </div>
            <div>
              <div class="text-12 text-gray-500">{{ $t('admin.security.projects.with_keys') }}</div>
              <div class="font-600 text-18">{{ countText(keys.projects?.with_keys) }}</div>
            </div>
          </div>

          <template v-if="keys.job">
            <Divider dense />
            <div class="flex justify-between items-center p-4 flex-wrap gap-3">
              <div>
                <div class="flex items-center flex-wrap gap-2">
                  <Badge info :text="$t('admin.security.job.running')" />
                  <span class="font-600">{{ jobKindLabel(keys.job.kind) }}</span>
                </div>
                <div class="text-13 mt-1">
                  {{ $t('admin.security.job.progress', { processed: keys.job.processed || 0, total: keys.job.total || 0 }) }}
                  <span v-if="keys.job.failed" class="text-alert"> · {{ $t('admin.security.job.failed', { count: keys.job.failed }) }}</span>
                </div>
                <div v-if="keys.job.retiring_generation" class="text-13">{{ $t('admin.security.job.retiring', { generation: keys.job.retiring_generation }) }}</div>
                <div v-if="keys.job.then" class="text-13">{{ $t('admin.security.job.then', { kind: jobKindLabel(keys.job.then) }) }}</div>
                <div class="text-12 text-gray-500 mt-1">
                  {{ $t('admin.security.job.started_by', { actor: keys.job.actor || $t('admin.security.history.system'), date: formatDate(keys.job.started_at) }) }}
                </div>
              </div>
              <PillButton outlined :text="$t('admin.security.job.cancel')" :loading="cancelling" :disabled="cancelling" @click="onCancelJobClick" />
            </div>
          </template>

          <div v-for="kind in JOB_KINDS" :key="kind">
            <Divider dense />
            <div class="flex justify-between items-center p-4 flex-wrap gap-3">
              <div>
                <div class="font-600">{{ $t(`admin.security.projects.${kind}`) }}</div>
                <div class="text-13">{{ $t(`admin.security.projects.${kind}_description`) }}</div>
              </div>
              <PillButton :text="$t(`admin.security.projects.${kind}`)" :loading="startingKind === kind" :disabled="jobRunning || startingKind !== null" @click="onJobClick(kind)" />
            </div>
          </div>
          <div v-if="jobRunning || jobsError" class="px-4 pb-4">
            <div v-if="jobRunning" class="text-12 text-semi-dark-text">{{ $t('admin.security.projects.job_running') }}</div>
            <FormError v-if="jobsError" :label="jobsError" />
          </div>

          <template v-if="keys.last_job">
            <Divider dense />
            <div class="p-4">
              <div class="font-600 mb-1">{{ $t('admin.security.job.last_title') }}</div>
              <div class="flex items-center flex-wrap gap-2">
                <Badge v-bind="lastJobBadge" />
                <span class="font-600">{{ jobKindLabel(keys.last_job.kind) }}</span>
              </div>
              <div class="text-13 mt-1">
                {{ $t('admin.security.job.progress', { processed: keys.last_job.processed || 0, total: keys.last_job.total || 0 }) }}
                <span v-if="keys.last_job.failed" class="text-alert"> · {{ $t('admin.security.job.failed', { count: keys.last_job.failed }) }}</span>
              </div>
              <div class="text-12 text-gray-500 mt-1">
                {{ $t('admin.security.job.started_by', { actor: keys.last_job.actor || $t('admin.security.history.system'), date: formatDate(keys.last_job.started_at) }) }}
                · {{ $t('admin.security.job.finished_at', { date: formatDate(keys.last_job.finished_at) }) }}
              </div>
              <template v-if="failureItems.length">
                <div class="font-600 text-13 mt-4">{{ $t('admin.security.job.failures') }}</div>
                <TableElevated :items="failureItems" :headers="failureHeaders" class="text-13">
                  <template v-slot:[`item.result`]="{ item }">
                    <Badge v-if="item.status" warn :text="item.status" />
                    <span v-else class="text-alert">{{ item.error }}</span>
                  </template>
                </TableElevated>
              </template>
            </div>
          </template>
        </div>

        <!-- 4. Needs attention -->
        <div class="mt-8 text-18 font-500 mb-1">{{ $t('admin.security.problems.title') }}</div>
        <div class="text-13 text-semi-dark-text mb-2">{{ $t('admin.security.problems.guidance') }}</div>
        <div v-if="!problemItems.length" class="text-13 text-gray-500">{{ $t('admin.security.problems.empty') }}</div>
        <TableElevated v-else :items="problemItems" :headers="problemHeaders" class="text-13">
          <template v-slot:[`item.details`]="{ item }">
            <div class="py-1">
              <div v-if="item.details">{{ item.details }}</div>
              <div v-if="item.unreadable" class="text-12 text-alert">{{ item.unreadable }}</div>
            </div>
          </template>
        </TableElevated>

        <!-- 5. History -->
        <div class="mt-8 text-18 font-500 mb-2">{{ $t('admin.security.history.title') }}</div>
        <div v-if="!historyItems.length" class="text-13 text-gray-500">{{ $t('admin.security.history.empty') }}</div>
        <TableElevated v-else :items="historyItems" :headers="historyHeaders" class="text-13" />
      </template>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions, mapMutations, mapState } from 'vuex';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import RotateRootKeyModal from '@/components/admin/RotateRootKeyModal.vue';

const JOB_KINDS = ['migrate', 'rotate', 'maintain'];
const KNOWN_CHECKS = ['keyring', 'keyring_tables', 'agents', 'slot_a', 'slot_b'];
const KNOWN_JOB_KINDS = [...JOB_KINDS, 'rewrap'];
const ROOT_STATES = ['active', 'retiring', 'retired'];
const POLL_INTERVAL_MS = 3000;
const FINGERPRINT_SHOWN = 16;
const UNREADABLE_SHOWN = 3;
const IN_USE_SLOT_STATES = ['active', 'retiring'];
const CREATE_KEY_COMMAND = 'openssl rand -base64 32';
const FINGERPRINT_COMMAND =
  "printf '%s' 'fidsy|root-key-fingerprint' | openssl dgst -sha256 -mac HMAC -macopt hexkey:$(printf '%s' \"<your key>\" | openssl base64 -d -A | od -An -tx1 | tr -d ' \\n') | awk '{print substr($NF,1,32)}'";

export default {
  name: 'AdminSecurity',
  data() {
    return {
      JOB_KINDS,
      CREATE_KEY_COMMAND,
      FINGERPRINT_COMMAND,
      loading: true,
      loadError: '',
      refreshing: false,
      pollTimer: null,
      checkingSlots: false,
      savingRoot: false,
      retiringGeneration: null,
      rootResult: '',
      rootError: '',
      startingKind: null,
      cancelling: false,
      jobsError: '',
    };
  },
  created() {
    this.loadKeys();
  },
  beforeUnmount() {
    this.stopPolling();
    this.SET_ADMIN_SECURITY_KEYS(null);
  },
  computed: {
    ...mapState({ keys: 'adminSecurityKeys' }),
    /**
     * @returns {boolean} Whether any root key generation exists.
     */
    hasRootKey() {
      return !!this.keys?.root_keys?.length;
    },
    /**
     * @returns {boolean} Whether a key job is running now.
     */
    jobRunning() {
      return !!this.keys?.job;
    },
    /**
     * @returns {Array<object>} Slots whose state needs the admin's attention (unbound or holding an unexpected value).
     */
    alertSlots() {
      return (this.keys?.slots || []).filter((slot) => ['mismatch', 'unbound'].includes(slot.holds));
    },
    /**
     * @returns {Array<object>} Slots still holding a retired root key, which the admin may clear.
     */
    retiredSlots() {
      return (this.keys?.slots || []).filter((slot) => slot.holds === 'retired' && slot.generation);
    },
    /**
     * @returns {(object|undefined)} Once a root key is registered, the slot not holding an active or retiring key.
     */
    freeSlot() {
      if (!this.hasRootKey) return undefined;
      return (this.keys?.slots || []).find((slot) => !IN_USE_SLOT_STATES.includes(slot.holds));
    },
    /**
     * @returns {(object|undefined)} The slot holding a new, unregistered key ready to use: slot A before the first registration, else the free slot.
     */
    newKeySlot() {
      const slot = this.hasRootKey ? this.freeSlot : (this.keys?.slots || []).find((s) => s.slot === 'A');
      return slot?.holds === 'new' && slot.fingerprint ? slot : undefined;
    },
    /**
     * @returns {string} Why the root key cannot be changed right now, or an empty string.
     */
    changeBlockedReason() {
      if (!this.hasRootKey) return '';
      if (this.jobRunning) return this.$t('admin.security.root.change_blocked_job');
      if (this.keys.root_keys.some((rootKey) => rootKey.state === 'retiring')) return this.$t('admin.security.root.change_blocked_retiring');
      return '';
    },
    /**
     * @returns {Array<object>} Table headers for the root key generations.
     */
    rootKeyHeaders() {
      return [
        { key: 'generation', title: this.$t('admin.security.root.generation'), width: '100px' },
        { key: 'slot', title: this.$t('admin.security.root.slot'), width: '60px' },
        { key: 'state', title: this.$t('admin.security.root.state'), width: '110px' },
        { key: 'created', title: this.$t('admin.security.root.created') },
        { key: 'project_keys', title: this.$t('admin.security.root.project_keys'), width: '110px' },
        { key: 'fingerprint', title: this.$t('admin.security.root.fingerprint'), width: '170px' },
        { key: 'actions', title: '', width: '100px' },
      ];
    },
    /**
     * @returns {Array<object>} Root key generations, newest first.
     */
    rootKeyItems() {
      return [...(this.keys?.root_keys || [])]
        .sort((a, b) => b.generation - a.generation)
        .map((rootKey) => ({
          ...rootKey,
          key: rootKey.generation,
          created: rootKey.created_by ? this.$t('admin.security.root.created_by', { date: this.formatDate(rootKey.created_at), actor: rootKey.created_by }) : this.formatDate(rootKey.created_at),
        }));
    },
    /**
     * @returns {object} Badge props describing how the last job ended.
     */
    lastJobBadge() {
      const { cancelled, failed } = this.keys.last_job;
      if (cancelled) return { gray: true, text: this.$t('admin.security.job.cancelled') };
      if (failed) return { warn: true, text: this.$t('admin.security.job.finished_with_failures') };
      return { success: true, text: this.$t('admin.security.job.finished') };
    },
    /**
     * @returns {Array<object>} Table headers for the last job's failures.
     */
    failureHeaders() {
      return [
        { key: 'project_id', title: this.$t('admin.security.job.failure_project'), width: '240px' },
        { key: 'result', title: this.$t('admin.security.job.failure_result'), width: '200px' },
        { key: 'unreadable', title: this.$t('admin.security.job.failure_unreadable') },
      ];
    },
    /**
     * @returns {Array<object>} The last job's failed projects.
     */
    failureItems() {
      return (this.keys?.last_job?.failures || []).map((failure, index) => ({
        key: index,
        project_id: failure.project_id,
        status: failure.status,
        error: failure.error,
        unreadable: (failure.unreadable || []).join(', '),
      }));
    },
    /**
     * @returns {Array<object>} Table headers for the problems list.
     */
    problemHeaders() {
      return [
        { key: 'project_id', title: this.$t('admin.security.problems.project'), width: '220px' },
        { key: 'event', title: this.$t('admin.security.problems.event'), width: '200px' },
        { key: 'details', title: this.$t('admin.security.problems.details') },
        { key: 'when', title: this.$t('admin.security.problems.when'), width: '160px' },
      ];
    },
    /**
     * @returns {Array<object>} Projects whose last key operation did not finish, newest first.
     */
    problemItems() {
      return [...(this.keys?.problems || [])]
        .sort((a, b) => b.at - a.at)
        .map((problem, index) => ({
          key: index,
          project_id: problem.project_id,
          event: this.eventLabel(problem.event),
          details: this.summarizeProblem(problem.detail),
          unreadable: this.unreadableText(problem.detail?.unreadable),
          when: this.formatDate(problem.at),
        }));
    },
    /**
     * @returns {Array<object>} Table headers for the history.
     */
    historyHeaders() {
      return [
        { key: 'event', title: this.$t('admin.security.history.event'), width: '220px' },
        { key: 'details', title: this.$t('admin.security.history.details') },
        { key: 'actor', title: this.$t('admin.security.history.actor'), width: '200px' },
        { key: 'when', title: this.$t('admin.security.history.when'), width: '160px' },
      ];
    },
    /**
     * @returns {Array<object>} History rows, newest first.
     */
    historyItems() {
      return [...(this.keys?.events || [])]
        .sort((a, b) => b.at - a.at)
        .map((e, index) => ({
          key: index,
          event: this.eventLabel(e.event),
          details: this.summarizeEvent(e.detail),
          actor: e.actor || this.$t('admin.security.history.system'),
          when: this.formatDate(e.at),
        }));
    },
  },
  methods: {
    ...mapActions(['fetchAdminSecurityKeys', 'registerAdminRootKey', 'changeAdminRootKey', 'retireAdminRootKey', 'startAdminSecurityJob', 'cancelAdminSecurityJob']),
    ...mapMutations(['SET_ADMIN_SECURITY_KEYS']),
    /**
     * Formats a millisecond timestamp the way the rest of the studio does.
     * @param {number} value Timestamp in milliseconds.
     * @returns {string} e.g. "Oct 11, 2026 14:05", or an empty string.
     */
    formatDate(value) {
      return value ? dayjs(value).format('MMM DD, YYYY HH:mm') : '';
    },
    /**
     * @param {(number|null)} value A count that may be unknown.
     * @returns {string} The count, or N/A.
     */
    countText(value) {
      return Number.isFinite(value) ? String(value) : this.$t('n_a');
    },
    /**
     * @param {string} name Check name, e.g. slot_a.
     * @returns {string} Readable label, or the name itself for an unknown check.
     */
    checkLabel(name) {
      return KNOWN_CHECKS.includes(name) ? this.$t(`admin.security.checks.${name}`) : name;
    },
    /**
     * @param {string} kind Job kind, e.g. migrate.
     * @returns {string} Readable label, or the kind itself for an unknown job.
     */
    jobKindLabel(kind) {
      return KNOWN_JOB_KINDS.includes(kind) ? this.$t(`admin.security.job.kinds.${kind}`) : kind;
    },
    /**
     * @param {string} state Root key state.
     * @returns {string} Readable label, or the state itself when unknown.
     */
    rootStateLabel(state) {
      return ROOT_STATES.includes(state) ? this.$t(`admin.security.root.state_${state}`) : state;
    },
    /**
     * @param {string} fingerprint Full fingerprint.
     * @returns {string} The first characters, enough to tell generations apart.
     */
    shortFingerprint(fingerprint) {
      if (!fingerprint || fingerprint.length <= FINGERPRINT_SHOWN) return fingerprint || '';
      return `${fingerprint.slice(0, FINGERPRINT_SHOWN)}…`;
    },
    /**
     * Platform events have their own labels; project events (problems) reuse the project Encryption Keys labels.
     * @param {string} event Event name, e.g. root.rotated or migration.blocked.
     * @returns {string} Readable label, or the name itself for an unknown event.
     */
    eventLabel(event) {
      const name = String(event).replace('.', '_');
      const key = [`admin.security.events.${name}`, `projects.encryption_keys.events.${name}`].find((k) => this.$te(k));
      return key ? this.$t(key) : event;
    },
    /**
     * Turns a platform event's detail into a short readable summary.
     * @param {(object|null)} detail Event detail.
     * @returns {string} Summary, or an empty string.
     */
    summarizeEvent(detail) {
      if (!detail) return '';
      const t = (key, values) => this.$t(`admin.security.detail.${key}`, values);
      const parts = [];
      if (detail.kind) parts.push(this.jobKindLabel(detail.kind));
      if (Number.isFinite(detail.generation)) parts.push(t('generation', detail));
      if (detail.slot) parts.push(t('slot', detail));
      if (Number.isFinite(detail.previous)) parts.push(t('replaced', detail));
      if (detail.rotate_project_keys) parts.push(t('rotate_project_keys'));
      if (detail.fingerprint) parts.push(t('fingerprint', detail));
      if (Number.isFinite(detail.total)) parts.push(t('total', { count: detail.total }));
      if (Number.isFinite(detail.processed)) parts.push(t('processed', { count: detail.processed }));
      if (detail.failed) parts.push(t('failed', { count: detail.failed }));
      if (detail.message) parts.push(detail.message);
      return parts.join(' · ');
    },
    /**
     * Summarises what is left over in a project's blocked or incomplete key operation.
     * @param {(object|null)} detail Problem detail.
     * @returns {string} e.g. "3 still to re-encrypt · attempt 4", or an empty string.
     */
    summarizeProblem(detail) {
      if (!detail) return '';
      const t = (key, values) => this.$t(`projects.encryption_keys.detail.${key}`, values);
      const parts = [];
      const remaining = Object.values(detail.remaining || {}).reduce((sum, n) => sum + n, 0) + (detail.legacy_remaining || 0);
      if (remaining) parts.push(t('remaining', { count: remaining }));
      if (detail.attempts > 1) parts.push(t('attempt', detail));
      return parts.join(' · ');
    },
    /**
     * Lists the first few secrets that could not be read, so the admin knows what to re-enter.
     * @param {(Array<string>|undefined)} items Field paths, e.g. aiprovider#OPENAI:key.
     * @returns {string} e.g. "Could not read: a, b, c (+2 more)", or an empty string.
     */
    unreadableText(items) {
      if (!Array.isArray(items) || !items.length) return '';
      const shown = items.slice(0, UNREADABLE_SHOWN).join(', ');
      const more = items.length > UNREADABLE_SHOWN ? ` (${this.$t('projects.encryption_keys.detail.more', { count: items.length - UNREADABLE_SHOWN })})` : '';
      return this.$t('projects.encryption_keys.detail.unreadable_list', { items: shown + more });
    },
    /**
     * @param {object} rootKey Root key row.
     * @returns {boolean} Whether this generation can be retired by hand now.
     */
    canRetire(rootKey) {
      return rootKey.state === 'retiring' && rootKey.project_keys === 0 && !this.jobRunning;
    },
    /**
     * Loads the key status for the page.
     * @returns {Promise<void>} Resolves once loaded or failed.
     */
    async loadKeys() {
      this.loading = true;
      const { error } = await this.fetchAdminSecurityKeys();
      this.loadError = error || '';
      this.loading = false;
    },
    /**
     * Reloads the key status in the background while a job runs; skipped if a reload is still in flight.
     * @returns {Promise<void>} Resolves once reloaded or failed.
     */
    async refresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      const { error } = await this.fetchAdminSecurityKeys();
      this.loadError = error || '';
      this.refreshing = false;
    },
    /**
     * @returns {void}
     */
    stopPolling() {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    /**
     * Names of the Secrets Store secret behind a root key slot.
     * @param {string} slot A or B.
     * @returns {{binding: string, names: string}} The Worker binding and the dev/prod secret names.
     */
    slotSecret(slot) {
      const lower = String(slot).toLowerCase();
      return {
        binding: `FIDSY_ROOT_KEY_${slot}`,
        names: this.$t('admin.security.root.secret_names', { dev: `fidsy-root-key-dev-${lower}`, prod: `fidsy-root-key-prod-${lower}` }),
      };
    },
    /**
     * Reloads the status so the slots show the key the admin just pasted.
     * @returns {Promise<void>} Resolves once reloaded or failed.
     */
    async onCheckSlotsClick() {
      this.checkingSlots = true;
      this.rootError = '';
      this.rootResult = '';
      const { error } = await this.fetchAdminSecurityKeys();
      this.rootError = error || '';
      this.checkingSlots = false;
    },
    /**
     * Asks for confirmation (repeating the fingerprint), then registers the key in slot A as the first root key.
     * @returns {void}
     */
    onRegisterClick() {
      const { slot, fingerprint } = this.newKeySlot;
      this.$showModal(DeleteModal, {
        title: this.$t('admin.security.root.register_confirm_title'),
        subtitle: this.$t('admin.security.root.register_confirm_subtitle', { slot, fingerprint }),
        confirm: this.$t('admin.security.root.register'),
        type: 'primary',
        onConfirm: async () => {
          this.savingRoot = true;
          this.rootError = '';
          this.rootResult = '';
          const { data, error } = await this.registerAdminRootKey({ fingerprint });
          this.savingRoot = false;
          if (error) this.rootError = error;
          else this.rootResult = this.$t('admin.security.root.registered', data.root_key || {});
        },
      });
    },
    /**
     * Asks for confirmation (repeating the fingerprint, and whether to rotate project keys too), then switches to the key in the free slot.
     * @returns {void}
     */
    onUseKeyClick() {
      const { slot, fingerprint } = this.newKeySlot;
      this.$showModal(RotateRootKeyModal, {
        slotName: slot,
        fingerprint,
        onConfirm: async (payload) => {
          this.savingRoot = true;
          this.rootError = '';
          this.rootResult = '';
          const { data, error } = await this.changeAdminRootKey(payload);
          this.savingRoot = false;
          if (error) this.rootError = error;
          else this.rootResult = this.$t('admin.security.root.changed', data.root_key || {});
        },
      });
    },
    /**
     * Asks for confirmation, then retires a root key generation whose automatic retirement failed.
     * @param {number} generation Generation to retire.
     * @returns {void}
     */
    onRetireClick(generation) {
      this.$showModal(DeleteModal, {
        title: this.$t('admin.security.root.retire_confirm_title', { generation }),
        subtitle: this.$t('admin.security.root.retire_confirm_subtitle', { generation }),
        confirm: this.$t('admin.security.root.retire'),
        onConfirm: async () => {
          this.retiringGeneration = generation;
          this.rootError = '';
          this.rootResult = '';
          const { error } = await this.retireAdminRootKey({ generation });
          this.rootError = error || '';
          this.retiringGeneration = null;
        },
      });
    },
    /**
     * Asks for confirmation, then starts a background job across all projects.
     * @param {string} kind migrate, rotate or maintain.
     * @returns {void}
     */
    onJobClick(kind) {
      const action = this.$t(`admin.security.projects.${kind}`);
      this.$showModal(DeleteModal, {
        title: this.$t('admin.security.projects.confirm_title', { action }),
        subtitle: this.$t(`admin.security.projects.${kind}_confirm`),
        confirm: action,
        type: 'primary',
        onConfirm: async () => {
          this.startingKind = kind;
          this.jobsError = '';
          const { error } = await this.startAdminSecurityJob({ kind });
          this.jobsError = error || '';
          this.startingKind = null;
        },
      });
    },
    /**
     * Cancels the running job; projects already processed stay processed.
     * @returns {Promise<void>} Resolves once cancelled or failed.
     */
    async onCancelJobClick() {
      this.cancelling = true;
      this.jobsError = '';
      const { error } = await this.cancelAdminSecurityJob();
      this.jobsError = error || '';
      this.cancelling = false;
    },
  },
  watch: {
    jobRunning: {
      /**
       * Polls the status every few seconds while a job runs, and stops when it finishes.
       * @param {boolean} running Whether a job is running.
       * @returns {void}
       */
      handler(running) {
        if (running && !this.pollTimer) {
          this.pollTimer = setInterval(this.refresh, POLL_INTERVAL_MS);
        } else if (!running) {
          this.stopPolling();
        }
      },
      immediate: true,
    },
  },
};
</script>
